import { Place } from "@mui/icons-material";
import {
  Box,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { ISearchFilter } from "../models";
import { selectFilter, searchStoreActions } from "../saga/searchStoreSlice";
import { selectDistricts } from "features/homepage/saga/homepageSlice";
import { IValueLabel } from "utils/commonModels";
import { IDistrictData } from "features/homepage/models";

function AreaPickerButton({
  onChange,
}: {
  onChange: (value: IValueLabel<string> | null) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  const filter = useAppSelector(selectFilter);
  const districtList = useAppSelector(selectDistricts);

  const [selectedArea, setSelectedArea] = useState<IValueLabel<string>|null>();

  const handleClick = (event: any) => {
    setAnchorEl(anchorEl ? null : buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectArea = (value: IDistrictData) => {
    setSelectedArea({
      value: value.PK,
      label: value.Name,
    });
    onChange({
      value: value.PK,
      label: value.Name,
    });
    handleClose();
  };

  const handleUnselect = (value: IDistrictData) => {
    setSelectedArea(null);
    onChange(null);
    handleClose();
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (filter.area) {
      setSelectedArea(filter.area);
    }
  }, [filter.area]);

  return (
    <>
      <Paper
        ref={buttonRef}
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1,
          my: 1,
          height: "26px",
          cursor: "pointer",
        }}
        onClick={handleClick}
      >
        <Place color="primary" fontSize="small" sx={{ px: 1 }} />
        <Typography>{selectedArea?.label}</Typography>
      </Paper>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="right-start"
        transition
        disablePortal
        modifiers={[
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: "document",
              padding: 8,
            },
          },
          {
            name: "offset",
            enabled: true,
            options: {
              offset: [-20, 20],
            },
          },
        ]}
        sx={{
          position: "relative",
          left: "20px",
          overflowX: "unset",
          overflowY: "unset",
          boxShadow: 1,
          zIndex: 5,
          "&::before": {
            zIndex: -11,
            boxShadow: "-8px 0px 6px -4px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            content: '""',
            display: "block",
            position: "absolute",
            width: 20,
            height: 20,
            top: "40px",
            transform: "translateY(-50%) rotate(45deg)",
            left: "-10px",
          },
        }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Fade {...TransitionProps} timeout={250}>
              <Box
                display="flex"
                bgcolor="white"
                zIndex={120}
                p={1}
                width="400px"
                maxHeight="500px"
                flexWrap="wrap"
              >
                {districtList &&
                  districtList.map((e) =>
                    selectedArea?.value === e.PK ? (
                      <Box
                        key={e.PK}
                        border={1}
                        borderRadius={2}
                        sx={{
                          cursor: "pointer",
                          padding: 1,
                          m: 1,
                          color: "primary.main",
                        }}
                        onClick={() => handleUnselect(e)}
                      >
                        <Typography variant="body2">{e.Name}</Typography>
                      </Box>
                    ) : (
                      <Box
                        key={e.PK}
                        border={1}
                        borderColor="divider"
                        borderRadius={2}
                        sx={{
                          padding: 1,
                          m: 1,
                          "&:hover": {
                            cursor: "pointer",
                            color: "primary.main",
                            borderColor: "primary.main",
                          },
                        }}
                        onClick={() => handleSelectArea(e)}
                      >
                        <Typography variant="body2">{e.Name}</Typography>
                      </Box>
                    )
                  )}
              </Box>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}

export default AreaPickerButton;
